'use client';

import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, Shield, Trash2 } from 'lucide-react';
import { useAuth } from '@/components/auth-provider';
import { useT } from '@/components/i18n-provider';
import { ProviderIcon } from '@/components/provider-icon';
import { Spinner } from '@/components/ui/spinner';
import { MessagingKillSwitchBanner } from '@/components/messaging-kill-switch-banner';
import { formatNanocreditsAsCredits } from '@/lib/balance/units';

export interface ConnectedApp {
  id: string;
  clientId: string;
  clientName: string;
  name?: string;
  logoUri?: string | null;
  redirectUris?: string[];
  scopes: string[] | null;
  expiresAt: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  limitDaily: string | null;
  limitTotal: string | null;
  spentToday: string;
  spentTotal: string;
}

interface ConnectedAccountsTabProps {
  connectedApps?: ConnectedApp[];
  onRevokeApp?: (appId: string) => Promise<void>;
  onSaveAppLimits?: (appId: string, dailyNano: string | null, totalNano: string | null) => Promise<void>;
  formatNanocredits?: (nanocredits: string | null) => string;
}

interface AgentOption {
  id: string;
  username: string;
  displayName?: string | null;
}

// `connect=1` (S-232, 2026-09-24): these start the same OAuth flow as the
// sign-in buttons, and the callback used to resolve the identity by email or
// create a new account even though the person is signed in here. With the
// flag it attaches the identity to THIS account, the same way Discord's
// connect does; see lib/auth/identity-linking.ts.
const IDENTITY_PROVIDERS = [
  {
    id: 'google',
    name: 'Google',
    slug: 'google',
    authUrl: '/api/auth/google?connect=1&returnTo=/settings',
  },
  {
    id: 'x',
    name: 'X / Twitter',
    slug: 'x',
    authUrl: '/api/auth/x?connect=1&returnTo=/settings',
  },
];

function CopyableUrl({ value }: { value: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded border border-border bg-background px-2 py-1 text-xs">
        {value}
      </code>
      <button
        type="button"
        className="btn-ghost p-1.5"
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        aria-label={t('integrations.copyMcpUrl')}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

export default function ConnectedAccountsTab({
  connectedApps = [],
  onRevokeApp,
  // The settings page passes its own localized formatter; this default is for
  // any other host. Credits at the ledger's 1e9 per credit: until 2026-09-22 it
  // divided by 1e11 and printed a `$`, so an app's spend showed at 1/100 of
  // the figure the gate (S-208) actually counts, with the wrong unit on it.
  formatNanocredits = (value) => formatNanocreditsAsCredits(value ? BigInt(value) : 0),
}: ConnectedAccountsTabProps) {
  const t = useT();
  const { connectedAccounts, refresh } = useAuth();
  const [origin, setOrigin] = useState('');
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [agentUsername, setAgentUsername] = useState('');
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [revokingApp, setRevokingApp] = useState<string | null>(null);

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/agents')
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled) return;
        const items = ((json?.agents ?? []) as AgentOption[]).filter((a) => a.username);
        setAgents(items);
        setAgentUsername((prev) => prev || items[0]?.username || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const accountsByProvider = useMemo(() => {
    const map = new Map<string, typeof connectedAccounts[number]>();
    for (const account of connectedAccounts) {
      if (!map.has(account.provider)) map.set(account.provider, account);
    }
    return map;
  }, [connectedAccounts]);

  const mcpUrl = origin ? `${origin}/mcp` : '/mcp';
  const selectedAgentUrl = agentUsername ? `/u/${agentUsername}?tab=settings&hub=1` : '';

  async function disconnect(providerId: string) {
    if (!confirm(t('settings.confirmDisconnectAccount'))) return;
    setDisconnecting(providerId);
    try {
      await fetch(`/api/auth/connect/${providerId}`, { method: 'DELETE' });
      await refresh();
    } finally {
      setDisconnecting(null);
    }
  }

  async function revokeApp(appId: string) {
    if (!onRevokeApp) return;
    setRevokingApp(appId);
    try {
      await onRevokeApp(appId);
    } finally {
      setRevokingApp(null);
    }
  }

  return (
    <div className="space-y-5">
      <MessagingKillSwitchBanner />

      <div className="rounded-lg border border-border p-4">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
            <Shield className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">{t('settings.accountsAndMcp')}</h2>
            <p className="text-xs text-muted-foreground">
              {t('settings.accountsMcpScopeNote')}
            </p>
          </div>
        </div>

        <div className="rounded-md border border-dashed border-border p-3 text-sm">
          <p className="mb-2 text-muted-foreground">
            {t('settings.accountsMcpAgentPrompt')}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              value={agentUsername}
              onChange={(event) => setAgentUsername(event.target.value)}
              className="input text-sm"
            >
              {agents.length === 0 ? (
                <option value="">{t('home.emptyAgents')}</option>
              ) : (
                agents.map((agent) => (
                  <option key={agent.id} value={agent.username}>
                    {agent.displayName || `@${agent.username}`}
                  </option>
                ))
              )}
            </select>
            <a
              href={selectedAgentUrl || '/'}
              className={`btn-primary text-center text-sm ${!selectedAgentUrl ? 'pointer-events-none opacity-50' : ''}`}
            >
              {t('settings.openAgentTools')}
            </a>
          </div>
        </div>
      </div>

      <section className="rounded-lg border border-border p-4">
        <h3 className="mb-3 text-sm font-semibold">{t('settings.signInAccounts')}</h3>
        <div className="space-y-2">
          {IDENTITY_PROVIDERS.map((provider) => {
            const account = accountsByProvider.get(provider.id);
            const connected = Boolean(account);
            return (
              <div
                key={provider.id}
                className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <ProviderIcon slug={provider.slug} size={20} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{provider.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {connected
                        ? account?.providerUsername || account?.providerUserId || t('actions.connected')
                        : t('settings.notConnected')}
                    </div>
                  </div>
                </div>
                {connected ? (
                  <button
                    type="button"
                    className="btn-ghost p-1.5 text-destructive"
                    disabled={disconnecting === provider.id}
                    onClick={() => disconnect(provider.id)}
                    aria-label={t('settings.disconnectProvider', { provider: provider.name })}
                  >
                    {disconnecting === provider.id ? <Spinner size="sm" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                ) : (
                  <a href={provider.authUrl} className="btn-outline text-xs">
                    {t('actions.connect')}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-border p-4">
        <h3 className="mb-2 text-sm font-semibold">{t('integrations.robutlerMcp')}</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          {t('settings.robutlerMcpEndpointNote')}
        </p>
        <CopyableUrl value={mcpUrl} />
      </section>

      <section className="rounded-lg border border-border p-4">
        <h3 className="mb-3 text-sm font-semibold">{t('settings.authorizedOauthApps')}</h3>
        {connectedApps.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('settings.noOauthAppsAuthorized')}</p>
        ) : (
          <div className="space-y-2">
            {connectedApps.map((app) => (
              <div
                key={app.id}
                className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {app.name || app.clientName || app.clientId}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('settings.spentTodayTotal', { today: formatNanocredits(app.spentToday), total: formatNanocredits(app.spentTotal) })}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {app.redirectUris?.[0] && (
                    <a
                      href={app.redirectUris[0]}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost p-1.5"
                      aria-label={t('settings.openApp')}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {onRevokeApp && (
                    <button
                      type="button"
                      onClick={() => revokeApp(app.id)}
                      disabled={revokingApp === app.id}
                      className="btn-ghost p-1.5 text-destructive"
                      aria-label={t('settings.revokeApp')}
                    >
                      {revokingApp === app.id ? <Spinner size="sm" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
